export interface HashrateVisualizer {
  // Current network hashrate in EH/s
  currentHashrate: number;
  
  // Historical reference points
  milestones: {
    firstExahash: number;    // ~2017
    currentMax: number;      // ~2024
    theoretical: number;     // Future projection
  };
}

export interface HashrateMetrics {
  completionPercentage: number;
  constructionSpeed: number;
  visualScale: number;
  segmentCount: number;
}

export class HashrateMapper implements HashrateVisualizer {
  public currentHashrate: number = 0;
  
  public readonly milestones = {
    firstExahash: 1.0,     // ~2017
    currentMax: 500.0,     // ~2024 realistic
    theoretical: 1000.0,   // Future theoretical maximum
  };
  
  // Configuration for visualization scaling
  private readonly config = {
    minCompletion: 0,      // 0% at 0 EH/s
    maxCompletion: 100,    // 100% at theoretical max
    segmentTotal: 1000,    // Total segments in Dyson sphere
    speedMultiplier: 10,   // Construction speed factor
  };
  
  constructor(initialHashrate: number = 0) {
    this.currentHashrate = initialHashrate;
  }
  
  /**
   * Convert hashrate to sphere completion percentage using logarithmic scale
   * This provides better visual distribution across the range
   */
  public hashrateToCompletion(hashrateEH: number): number {
    if (hashrateEH <= 0) return 0;
    
    // Use logarithmic scale for better visual distribution
    // 0 EH/s = 0% complete
    // 500 EH/s = ~70% complete
    // 1000 EH/s = 100% complete
    const logValue = Math.log10(hashrateEH + 1);
    const logMax = Math.log10(this.milestones.theoretical + 1);
    
    return Math.min((logValue / logMax) * 100, 100);
  }
  
  /**
   * Calculate construction speed based on hashrate change rate
   */
  public getConstructionSpeed(hashrateChange: number): number {
    // Faster construction when hashrate is increasing rapidly
    const baseSpeed = this.config.speedMultiplier;
    const changeBonus = Math.max(0, hashrateChange) * 2;
    
    return baseSpeed + changeBonus;
  }
  
  /**
   * Get fleet size for mining pool visualization based on their hashrate share
   */
  public getFleetSize(poolHashrate: number): number {
    if (this.currentHashrate <= 0 || poolHashrate <= 0) return 0;
    
    const sharePercentage = (poolHashrate / this.currentHashrate) * 100;
    
    // Scale fleet size: 1% = 1 drone, 50% = 50 drones (max)
    return Math.min(Math.max(Math.floor(sharePercentage), 1), 50);
  }
  
  /**
   * Get complete metrics for current hashrate
   */
  public getMetrics(): HashrateMetrics {
    const completionPercentage = this.hashrateToCompletion(this.currentHashrate);
    const segmentCount = Math.floor((completionPercentage / 100) * this.config.segmentTotal);
    
    return {
      completionPercentage,
      constructionSpeed: this.getConstructionSpeed(0), // Default for current state
      visualScale: this.getVisualScale(),
      segmentCount,
    };
  }
  
  /**
   * Get visual scale factor for effects intensity
   */
  public getVisualScale(): number {
    // Scale from 0.5 to 2.0 based on hashrate
    const normalizedHashrate = this.currentHashrate / this.milestones.theoretical;
    return 0.5 + (normalizedHashrate * 1.5);
  }
  
  /**
   * Update current hashrate and return change metrics
   */
  public updateHashrate(newHashrate: number): {
    previousHashrate: number;
    currentHashrate: number;
    change: number;
    changePercentage: number;
    metrics: HashrateMetrics;
  } {
    const previousHashrate = this.currentHashrate;
    this.currentHashrate = newHashrate;
    
    const change = newHashrate - previousHashrate;
    const changePercentage = previousHashrate > 0 ? (change / previousHashrate) * 100 : 0;
    
    return {
      previousHashrate,
      currentHashrate: newHashrate,
      change,
      changePercentage,
      metrics: this.getMetrics(),
    };
  }
  
  /**
   * Get hashrate milestone information for UI/debugging
   */
  public getMilestoneInfo(): {
    current: {
      hashrate: number;
      completion: number;
      milestone: string;
    };
    next: {
      hashrate: number;
      completion: number;
      milestone: string;
    } | null;
  } {
    const currentCompletion = this.hashrateToCompletion(this.currentHashrate);
    
    let currentMilestone = 'Starting';
    let nextMilestone: { hashrate: number; completion: number; milestone: string; } | null = null;
    
    if (this.currentHashrate >= this.milestones.theoretical) {
      currentMilestone = 'Theoretical Maximum';
    } else if (this.currentHashrate >= this.milestones.currentMax) {
      currentMilestone = 'Current Era Peak';
      nextMilestone = {
        hashrate: this.milestones.theoretical,
        completion: 100,
        milestone: 'Theoretical Maximum',
      };
    } else if (this.currentHashrate >= this.milestones.firstExahash) {
      currentMilestone = 'Exahash Era';
      nextMilestone = {
        hashrate: this.milestones.currentMax,
        completion: this.hashrateToCompletion(this.milestones.currentMax),
        milestone: 'Current Era Peak',
      };
    } else {
      currentMilestone = 'Early Era';
      nextMilestone = {
        hashrate: this.milestones.firstExahash,
        completion: this.hashrateToCompletion(this.milestones.firstExahash),
        milestone: 'First Exahash',
      };
    }
    
    return {
      current: {
        hashrate: this.currentHashrate,
        completion: currentCompletion,
        milestone: currentMilestone,
      },
      next: nextMilestone,
    };
  }
  
  /**
   * Calculate time to reach target hashrate at current growth rate
   */
  public timeToTarget(targetHashrate: number, growthRatePerDay: number): number | null {
    if (growthRatePerDay <= 0 || targetHashrate <= this.currentHashrate) {
      return null;
    }
    
    const difference = targetHashrate - this.currentHashrate;
    return difference / growthRatePerDay; // Days
  }
  
  /**
   * Get segments that should be under construction based on completion percentage
   */
  public getConstructionSegments(targetCompletion: number): {
    completed: number[];
    underConstruction: number[];
    empty: number[];
  } {
    const currentCompletion = this.hashrateToCompletion(this.currentHashrate);
    const totalSegments = this.config.segmentTotal;
    
    const completedCount = Math.floor((currentCompletion / 100) * totalSegments);
    const targetCount = Math.floor((targetCompletion / 100) * totalSegments);
    const constructionCount = Math.min(10, targetCount - completedCount); // Max 10 segments building at once
    
    const completed: number[] = [];
    const underConstruction: number[] = [];
    const empty: number[] = [];
    
    for (let i = 0; i < totalSegments; i++) {
      if (i < completedCount) {
        completed.push(i);
      } else if (i < completedCount + constructionCount) {
        underConstruction.push(i);
      } else {
        empty.push(i);
      }
    }
    
    return { completed, underConstruction, empty };
  }
}

// Singleton instance for global use
export const hashrateMapper = new HashrateMapper();

// Utility functions for common operations
export const hashrateUtils = {
  /**
   * Format hashrate for display
   */
  formatHashrate(hashrateEH: number): string {
    if (hashrateEH >= 1000) {
      return `${(hashrateEH / 1000).toFixed(1)}ZH/s`;
    } else if (hashrateEH >= 1) {
      return `${hashrateEH.toFixed(1)}EH/s`;
    } else {
      return `${(hashrateEH * 1000).toFixed(0)}PH/s`;
    }
  },
  
  /**
   * Get color based on hashrate growth
   */
  getHashrateColor(changePercentage: number): string {
    if (changePercentage > 5) return '#00ff88'; // Strong growth - green
    if (changePercentage > 0) return '#88ff00'; // Positive growth - yellow-green
    if (changePercentage > -5) return '#ffaa00'; // Slight decline - orange
    return '#ff4444'; // Strong decline - red
  },
  
  /**
   * Get descriptive text for hashrate level
   */
  getHashrateDescription(hashrateEH: number): string {
    if (hashrateEH >= 1000) return 'Theoretical maximum security';
    if (hashrateEH >= 500) return 'Peak network security';
    if (hashrateEH >= 100) return 'High security level';
    if (hashrateEH >= 10) return 'Moderate security';
    if (hashrateEH >= 1) return 'Early security build-up';
    return 'Initial security phase';
  },
};